export function dayIndexRange(start, end) {
	if (!Number.isInteger(start) || !Number.isInteger(end)) return [];

	const from = Math.min(start, end);
	const to = Math.max(start, end);
	const range = [];

	for (let index = from; index <= to; index += 1) {
		range.push(index);
	}

	return range;
}

export function toggleDayIndex(selected, index) {
	const current = Array.isArray(selected) ? selected : [];
	if (!Number.isInteger(index)) return current;
	if (current.includes(index)) return current.filter((day) => day !== index);
	return [...current, index];
}

export function selectDayRange(anchor, index) {
	if (!Number.isInteger(index)) return [];
	const start = Number.isInteger(anchor) ? anchor : index;
	return dayIndexRange(start, index);
}

export function selectionFromDrag({
	startIndex,
	endIndex,
	base = [],
	additive = false,
}) {
	const range = dayIndexRange(startIndex, endIndex);
	if (!additive) return range;

	return [...new Set([...(base || []), ...range])].sort((a, b) => a - b);
}

export function trimDaySelection(selected, length) {
	const max = Number(length);
	if (!Number.isFinite(max)) return [];

	return (selected || []).filter(
		(index) => Number.isInteger(index) && index >= 0 && index < max
	);
}

const DAY_SELECTION_KEEP_SELECTOR =
	".sp-day-cell, .sp-week-total, .sp-assign-mode-banner__paste, .react-contextmenu";

/**
 * Un clic fuera de los días suelta la selección.
 * Los propios días, el contador de la semana, Pegar turnos y el menú contextual siguen su gesto.
 */
export function clickClearsDaySelection(
	target,
	{ hasSelection = false, suppressClick = false } = {}
) {
	const element = target && target.nodeType === 3 ? target.parentElement : target;
	if (suppressClick || !hasSelection) return false;
	if (!element || typeof element.closest !== "function") return false;
	if (element.closest(DAY_SELECTION_KEEP_SELECTOR)) return false;
	return true;
}

/**
 * Sin días marcados, un clic marca solo ese.
 * Si ya hay alguno, el clic suelta toda la selección.
 */
export function selectionAfterPlainDayClick(selected, index) {
	const current = Array.isArray(selected) ? selected : [];
	if (!Number.isInteger(index)) return current;
	if (current.length > 0) return [];
	return [index];
}

/** Si la fila ya está marcada entera, la suelta. Si no, deja solo esa fila. */
export function toggleRowSelection(selected, rowIndexes) {
	const row = (rowIndexes || []).filter((index) => Number.isInteger(index));
	const current = Array.isArray(selected) ? selected : [];
	if (row.length === 0) return current;

	const allSelected = row.every((index) => current.includes(index));
	if (allSelected) {
		const drop = new Set(row);
		return current.filter((index) => !drop.has(index));
	}

	return [...row];
}

export function cloneShiftUbications(shiftUbications) {
	return (shiftUbications || []).map((shiftUbication) => ({
		shift: shiftUbication.shift,
		ubications: [...(shiftUbication.ubications || [])],
	}));
}

export function cloneDayPattern(item) {
	return cloneShiftUbications(item?.shift_ubications);
}

/** Reparte el patrón copiado sobre los días destino, repitiéndolo si hacen falta. */
export function mapPatternPaste(pattern, targetIndexes) {
	const slots = pattern || [];
	const targets = [
		...new Set((targetIndexes || []).filter((index) => Number.isInteger(index))),
	].sort((a, b) => a - b);

	if (slots.length === 0 || targets.length === 0) return [];

	return targets.map((index, position) => ({
		index,
		shiftUbications: cloneShiftUbications(slots[position % slots.length]),
	}));
}

/**
 * Un solo día marcado: el bloque copiado empieza ahí.
 * Varios días marcados: el pegado cae en esos días.
 * Sin marca, el patrón empieza en el ancla y ocupa tantos días como tenga.
 */
export function patternTargetIndexes(selected, anchor, patternLength, dayCount) {
	const targets = [
		...new Set((selected || []).filter((index) => Number.isInteger(index))),
	].sort((a, b) => a - b);
	const length = Number(patternLength);
	const total = Number(dayCount);

	const fromStart = (start) => {
		if (!Number.isInteger(start) || !Number.isFinite(length) || length <= 0) {
			return [];
		}
		if (!Number.isFinite(total)) return [];

		const indexes = [];
		for (let offset = 0; offset < length; offset += 1) {
			const index = start + offset;
			if (index >= 0 && index < total) indexes.push(index);
		}
		return indexes;
	};

	if (targets.length === 1) return fromStart(targets[0]);
	if (targets.length > 1) return targets;

	return fromStart(anchor);
}

/** Días a los que aplicar «Pegar turnos»: la selección, más el día del menú si no estaba. */
export function resolvePasteDayIndexes(selected, contextIndex) {
	const current = (selected || []).filter((index) => Number.isInteger(index));
	const hasContext = Number.isInteger(contextIndex);

	if (!hasContext) return current;
	if (current.length === 0) return [contextIndex];
	if (current.includes(contextIndex)) return current;

	return [...current, contextIndex];
}
